import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Shell from '../components/Shell';
import ProtectedRoute from '../components/ProtectedRoute';
import { Hero, About, Events, Gallery, GetInvolved } from '../components/Sections';
import { News } from '../components/News';
import LoginPage from '../pages/LoginPage';
import SettingsPage from '../pages/SettingsPage';
import EventsPage from '../pages/EventsPage';
import NewsPage from '../pages/NewsPage';
import AdminPage from '../pages/AdminPage';
import ComingSoon from '../pages/ComingSoon';
import PageLoader from '../components/PageLoader';
import useSession from '../hooks/useSession';
import { trackPageView } from '../firebase/analytics';

// Log a GA4 page_view on each client-side navigation. Firebase's automatic
// measurement already logs a page_view for the initial document load, so we
// skip the first render to avoid double-counting it. Keying off path+search
// (not the whole location) means hash-only changes from in-page scroll links
// (/#about) don't fire a duplicate view for the same page.
function usePageViewTracking() {
  const location = useLocation();
  const page = location.pathname + location.search;
  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    trackPageView(page);
  }, [page]);
}

// Preview bypass: `?preview=true` unlocks the pre-launch site for the browser
// session so the sign-up / login flow can be tested on the hosted build. This
// only hides "Coming Soon"; real access is still enforced by the claim gates.
const PREVIEW_STORAGE_KEY = 'preview-unlocked';
function isPreviewUnlocked() {
  try {
    if (new URLSearchParams(window.location.search).get('preview') === 'true') {
      sessionStorage.setItem(PREVIEW_STORAGE_KEY, 'true');
      return true;
    }
    return sessionStorage.getItem(PREVIEW_STORAGE_KEY) === 'true';
  } catch (_) {
    return new URLSearchParams(window.location.search).get('preview') === 'true';
  }
}

// Home: private until launch — only admins (or dev / ?preview) see the full
// site; everyone else gets Coming Soon. The Events section is members-only.
function HomePage() {
  const { loading, user, isAdmin, hasMember } = useSession();
  const forceFullSite = process.env.NODE_ENV === 'development' || isPreviewUnlocked();
  // Wait for the session to resolve before choosing a view — otherwise an
  // authenticated admin sees "Coming Soon" flash before their claim loads.
  // (Preview / dev bypass doesn't depend on auth, so it need not wait.)
  if (loading && !forceFullSite) return <PageLoader />;
  const showFullSite = forceFullSite || isAdmin;
  if (!showFullSite) return <ComingSoon />;
  return (
    <Shell>
      <Hero />
      <About />
      {hasMember && <Events />}
      {hasMember && <News />}
      <Gallery />
      {/* The sign-in / create-account CTA is only useful to signed-out
          visitors. `!loading` keeps it from flashing on the preview / dev
          path, which renders before the session resolves. */}
      {!loading && !user && <GetInvolved />}
    </Shell>
  );
}

// The app's route table + per-route authorization, all in one place.
export default function AppRoutes() {
  usePageViewTracking();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<HomePage />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requires="auth">
            <Shell>
              <SettingsPage />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute requires="member">
            <Shell>
              <EventsPage />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute requires="member">
            <Shell>
              <NewsPage />
            </Shell>
          </ProtectedRoute>
        }
      />
      {/* Admin requires sign-in here; AdminPage shows "access denied" to
          signed-in non-admins and handles sub-paths like /admin/approve. */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requires="auth">
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
